import type { FC } from "react";
import { useEffect, useState } from "react";
import Link from "next/link";
import styled from "styled-components";
import type {
	IHelpsiteArticle,
	IHelpsiteArticleFields,
	IModuleFooterFields,
} from "@types-contentful";
import { colours } from "@helpers";
import FooterModule from "@components/FooterModule";
import Icon from "@components/Icon";
import HelpBreadcrumb from "./HelpBreadcrumb";
import HelpArticleContent from "./HelpArticleContent";
import HelpRelatedArticles from "./HelpRelatedArticles";
import HelpMobileSupportFooter from "./HelpMobileSupportFooter";
import HelpDesktopSupportFooter from "./HelpDesktopSupportFooter";

const PageWrapper = styled.div`
	width: 100%;
	box-sizing: border-box;
	overflow-x: hidden;
	/* Remove extra margin at bottom of FooterModule on this page */
	margin-bottom: 0 !important;
`;

const CONTENT_PADDING_MOBILE = "15px";
const CONTENT_PADDING_DESKTOP = "24px";
const CONTENT_MAX_WIDTH = "1136px";
const BREAKPOINT_1024 = "1024px";

/* Match HelpTopic: white header section, same padding and max-width */
const ArticleHeaderSection = styled.div`
	background-color: ${colours.WHITE};
	padding: 30px ${CONTENT_PADDING_MOBILE} 40px;
	width: 100%;
	box-sizing: border-box;

	@media (min-width: ${BREAKPOINT_1024}) {
		max-width: ${CONTENT_MAX_WIDTH};
		margin-left: auto;
		margin-right: auto;
		padding: 40px ${CONTENT_PADDING_DESKTOP} 50px;
	}
`;

/* Header divider: same width as header, solid line */
const HeaderDivider = styled.div`
	width: 100%;
	height: 1px;
	background: rgba(0, 0, 0, 0.5);
`;

/* Match HelpTopic TopicHeader: Taviraj light, sizes, midnight blue */
const ArticleHeader = styled.h1`
	font-family: var(--taviraj-light);
	font-weight: 400;
	font-size: 30px;
	line-height: 1.4em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0 0 16px;

	@media (min-width: ${BREAKPOINT_1024}) {
		font-size: 44px;
		line-height: 1.4em;
	}
`;

const ContentContainer = styled.div`
	width: 100%;
	box-sizing: border-box;
	padding: 0 ${CONTENT_PADDING_MOBILE} 40px;

	@media (min-width: ${BREAKPOINT_1024}) {
		max-width: ${CONTENT_MAX_WIDTH};
		margin-left: auto;
		margin-right: auto;
		padding: 0 ${CONTENT_PADDING_DESKTOP} 60px;
	}
`;

const ContentSection = styled.section`
	margin-top: 32px;
	margin-bottom: 48px;

	@media (min-width: ${BREAKPOINT_1024}) {
		margin-top: 48px;
	}
`;

const RelatedSection = styled.section`
	margin-top: 64px;
	padding-top: 48px;
	border-top: 1px solid #e0e0e0;
`;

const BrowseAllArticlesWrapper = styled.div`
	display: flex;
	justify-content: flex-start;
	width: 100%;
	box-sizing: border-box;
	padding: 24px ${CONTENT_PADDING_MOBILE} 40px;

	@media (min-width: ${BREAKPOINT_1024}) {
		max-width: ${CONTENT_MAX_WIDTH};
		margin-left: auto;
		margin-right: auto;
		padding: 0px ${CONTENT_PADDING_DESKTOP} 40px;
	}
`;

const BrowseAllArticlesLink = styled(Link)`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 12px 20px 12px;
	background-color: ${colours.WHITE};
	border: 1px solid rgba(0, 0, 0, 0.2);
	border-radius: 12px;
	text-decoration: none;
	color: ${colours.MIDNIGHT_BLUE};
	font-family: var(--euclid-regular);
	font-size: 16px;
	line-height: 1.27;
	box-sizing: border-box;

	&:hover {
		opacity: 0.9;
		color: ${colours.MIDNIGHT_BLUE};
		text-decoration: none;
	}
`;

const HelpSupportFooterWrapper = styled.div`
	margin-bottom: 60px;

	@media (min-width: ${BREAKPOINT_1024}) {
		margin-bottom: 80px;
	}
`;

interface HelpArticleProps {
	pageData: IHelpsiteArticleFields;
	footer?: IModuleFooterFields | null;
	/** Parent topic for breadcrumb (Help > Topic) */
	parentTopic?: { title: string; href: string };
	/** Article entry id -> parent topic slug segment */
	articleTopicSlugById?: Record<string, string>;
}

const HelpArticle: FC<HelpArticleProps> = ({
	pageData,
	footer,
	parentTopic,
	articleTopicSlugById = {},
}) => {
	const { articleMainHeader, mainContent, relatedArticles } = pageData;
	const relatedArticleEntries: IHelpsiteArticle[] = (relatedArticles ?? []).filter(
		(entry): entry is IHelpsiteArticle => {
			if (!entry?.sys?.id || entry?.sys?.contentType?.sys?.id !== "helpsiteArticle") {
				return false;
			}

			const fields = entry.fields as Partial<IHelpsiteArticleFields> | undefined;
			return !!fields?.articleRelativeUrl && !!fields?.articleMainHeader;
		},
	);

	const [openDropdownSlug, setOpenDropdownSlug] = useState<string | null>(null);

	useEffect(() => {
		const hash = typeof window !== "undefined" ? window.location.hash : "";
		if (hash.startsWith("#")) {
			const slug = hash.slice(1).toLowerCase().replace(/_/g, "-");
			if (slug) {
				setOpenDropdownSlug(slug);
				requestAnimationFrame(() => {
					document.getElementById(slug)?.scrollIntoView({
						behavior: "smooth",
						block: "start",
					});
				});
			}
		}
	}, []);

	const breadcrumbItems = [
		{ label: "Help", href: "/help" },
		...(parentTopic
			? [{ label: parentTopic.title, href: parentTopic.href }]
			: []),
	];

	/* Same destination as breadcrumb topic link (HelpRelatedArticles browse-all) */
	const browseAllArticlesHref = parentTopic?.href ?? "/help";
	const currentTopicSlug = parentTopic?.href
		? parentTopic.href.replace(/^\/help\/?/, "")
		: undefined;

	return (
		<PageWrapper>
			<HelpBreadcrumb items={breadcrumbItems} />
			<ArticleHeaderSection>
				{articleMainHeader && (
					<ArticleHeader>{articleMainHeader}</ArticleHeader>
				)}
				<HeaderDivider />
			</ArticleHeaderSection>
			<ContentContainer>
				{mainContent && mainContent.length > 0 && (
					<ContentSection>
						{mainContent.map((contentItem) => (
							<HelpArticleContent
								key={contentItem.sys.id}
								content={contentItem.fields}
								openDropdownSlug={openDropdownSlug}
								setOpenDropdownSlug={setOpenDropdownSlug}
							/>
						))}
					</ContentSection>
				)}

				{relatedArticleEntries.length > 0 && (
					<RelatedSection>
						<HelpRelatedArticles
							relatedArticles={relatedArticleEntries}
							articleTopicSlugById={articleTopicSlugById}
							currentTopicSlug={currentTopicSlug}
						/>
					</RelatedSection>
				)}
			</ContentContainer>

			<BrowseAllArticlesWrapper>
				<BrowseAllArticlesLink href={browseAllArticlesHref}>
					<Icon
						iconName="chevron-left"
						iconSizes={{
							iconMobile: 20,
							iconTablet: 20,
							iconLaptop: 20,
							iconDesktop: 20,
							iconDesktopLarge: 20,
						}}
						alt=""
					/>
					Browse all articles
				</BrowseAllArticlesLink>
			</BrowseAllArticlesWrapper>

			<HelpSupportFooterWrapper>
				<HelpMobileSupportFooter />
				<HelpDesktopSupportFooter />
			</HelpSupportFooterWrapper>
			{footer && <FooterModule {...footer} />}
		</PageWrapper>
	);
};

export default HelpArticle;
